import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSalonStore } from "@/lib/store/salon";
import { useUpdateWorkerEmail } from "../hooks/use-invitations";
import type { TeamMember, UserInvitation } from "../types";

// Email change form schema factory
const createEmailChangeSchema = (t: any) =>
  z.object({
    email: z.string().email(t("teamManagement.validEmailRequired")),
  });

type EmailChangeFormData = z.infer<ReturnType<typeof createEmailChangeSchema>>;

interface EmailChangeDialogProps {
  teamMember: TeamMember;
  currentEmail?: string;
  currentInvitation?: UserInvitation;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Optional callback when action succeeds
}

export function EmailChangeDialog({
  teamMember,
  currentEmail,
  currentInvitation,
  isOpen,
  onClose,
  onSuccess,
}: EmailChangeDialogProps) {
  const { t } = useTranslation("settings");
  // GraphQL hooks and state
  const [updateWorkerEmail, { loading: isLoading }] = useUpdateWorkerEmail();
  const currentSalon = useSalonStore((state) => state.currentSalon);

  const emailChangeSchema = createEmailChangeSchema(t);
  const form = useForm<EmailChangeFormData>({
    resolver: zodResolver(emailChangeSchema),
    defaultValues: {
      email: currentEmail || currentInvitation?.email || "",
    },
  });

  const onSubmit = async (data: EmailChangeFormData) => {
    if (!currentSalon?.id) {
      toast.error("No salon selected. Please refresh the page and try again.");
      return;
    }

    try {
      const result = await updateWorkerEmail({
        variables: {
          salonId: currentSalon.id,
          workerId: teamMember.workerId, // Use workerId (Worker ID), not id (WorkerSalon ID)
          input: {
            email: data.email,
            sendInvitation: true, // Always send invitation
          },
        },
      });

      // Check if mutation was successful
      if (result.data?.updateWorkerEmail) {
        // Show success message
        toast.success("Invitation has been sent to the worker's email.");

        onSuccess?.(); // Call success callback if provided
        onClose();
      } else {
        // Mutation returned false or null
        toast.error("Failed to update email. Please try again.");
      }
    } catch (error: any) {
      console.error("Failed to update email:", error);

      // Extract meaningful error message
      let errorMessage = "An unexpected error occurred. Please try again.";
      if (error?.graphQLErrors?.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("teamManagement.changeEmail")}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("business.email")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder={t("placeholders.enterEmail")}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? t("common.saving") : t("actions.update")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
